import { setup } from 'xstate';

import {
  type OrdersMachineContext,
  type OrdersMachineEvents,
  type OrdersMachineInput,
  type OrderWithIdAndWantedTime,
} from './orders-machine.types';
import { createOrdersMachineActions } from './setup/orders-machine.actions';
import { createOrdersMachineActors } from './setup/orders-machine.actors';
import { createOrdersMachineGuards } from './setup/orders-machine.guards';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A state machine to manage the user's placed orders history with
 * pagination support.
 *
 * NOTE: The machine is also "restartable", which means it can be restarted
 *       at any point during its flow by resetting the context and fetching
 *       the initial orders.
 *
 * NOTE: The machine requires external services to retrieve orders and
 *       cancellation statuses.
 *
 * NOTE: The initial state can be passed to the machine as an argument, however,
 *       it is usually used for testing.
 */
export const createOrdersMachine = <Order extends OrderWithIdAndWantedTime>(
  initialState: 'waiting' | 'idle' | 'fetchingInitialOrders' | 'fetchingMoreOrders' = 'waiting',
) => {
  return setup({
    types: {
      input: {} as OrdersMachineInput<Order>,
      context: {} as OrdersMachineContext<Order>,
      events: {} as OrdersMachineEvents,
    },
    actions: createOrdersMachineActions<Order>(),
    actors: createOrdersMachineActors<Order>(),
    guards: createOrdersMachineGuards<Order>(),
  }).createMachine({
    id: 'orders-state-machine',
    context: ({ input }) => ({
      orders: [],
      ordersPerPage: 10,
      page: 1,
      canFetchMoreOrders: false,
      ...input,
    }),
    initial: initialState,
    states: {
      waiting: {
        description: 'An empty state used for the initial setup.',
      },
      idle: {
        id: 'idle',
        description: 'An idle state that awaits the next event.',
        on: {
          REFETCH_ORDERS: {
            description: 'Refetch current orders.',
            target: '#refetching-orders',
          },
          FETCH_MORE_ORDERS: {
            description: 'Fetch more orders if possible.',
            target: '#fetching-more-orders',
            actions: ['increasePage'],
            guard: 'checkIfCanFetchMoreOrders',
          },
        },
      },
      fetchingInitialOrders: {
        id: 'fetching-initial-orders',
        initial: 'orders',
        states: {
          orders: {
            description: 'A state for fetching initial orders.',
            invoke: {
              id: 'fetchOrders',
              src: 'fetchOrders',
              input: ({ context }) => context,
              onDone: {
                target: '#idle',
                actions: [{ type: 'onFetchOrdersDone', params: ({ event }) => event.output }],
              },
              onError: {
                actions: [
                  { type: 'logFetchOrdersError', params: ({ event }) => ({ error: event.error }) },
                ],
              },
            },
          },
        },
      },
      refetchingOrders: {
        id: 'refetching-orders',
        initial: 'orders',
        states: {
          orders: {
            description: 'A state for refetching current orders.',
            invoke: {
              id: 'fetchOrders',
              src: 'fetchOrders',
              input: ({ context }) => context,
              onDone: {
                target: '#idle',
                actions: [{ type: 'onFetchOrdersDone', params: ({ event }) => event.output }],
              },
              onError: {
                actions: [
                  { type: 'logFetchOrdersError', params: ({ event }) => ({ error: event.error }) },
                ],
              },
            },
          },
        },
      },
      fetchingMoreOrders: {
        id: 'fetching-more-orders',
        initial: 'orders',
        states: {
          orders: {
            description: 'A state for fetching more orders.',
            invoke: {
              id: 'fetchOrders',
              src: 'fetchOrders',
              input: ({ context }) => context,
              onDone: {
                target: '#idle',
                actions: [{ type: 'onFetchOrdersDone', params: ({ event }) => event.output }],
              },
              onError: {
                actions: [
                  { type: 'logFetchOrdersError', params: ({ event }) => ({ error: event.error }) },
                ],
              },
            },
          },
        },
      },
    },
    on: {
      START: {
        description:
          'Starts/restarts the machine by fetching the initial orders and clearing the state.',
        target: '#fetching-initial-orders',
        actions: ['resetContext'],
      },
    },
  });
};
